import { useRuntimeConfig } from 'nuxt/app';

interface TweakwiseProduct {
    id: string;
    key: string;
    title: string;
    price: number;
    url: string;
    imageUrl?: string;
    attributes?: Record<string, any>;
    [key: string]: any;
}

interface TweakwiseRecommendationsResponse {
    products: TweakwiseProduct[];
    statusCode?: number;
    error?: string;
}

// Define the TweakwisePlugin interface to match the plugin implementation
interface TweakwisePlugin {
    getVisitorId: () => string;
    trackPurchase: (productIDs: string[]) => void;
    trackProductView: (productID: string) => void;
    trackSearch: (searchTerm: string) => void;
}

// Extend NuxtApp to include the $tweakwise property
declare module '#app' {
    interface NuxtApp {
        $tweakwise: TweakwisePlugin;
    }
}

/**
 * Composable for interacting with Tweakwise API
 */
export function useTweakwise() {
    const config = useRuntimeConfig();
    const instanceKey = config.public.tweakwise.instanceKey;
    let profileKey = '';

    // Try to get profileKey from tweakwise plugin on client side
    if (process.client) {
        const nuxtApp = useNuxtApp();
        if (nuxtApp.$tweakwise) {
            profileKey = nuxtApp.$tweakwise.getVisitorId();
        }
    }

    /**
     * JSON is requested through the Accept header — the `format=json` query
     * parameter is no longer supported. Without it the gateway answers in XML.
     */
    const JSON_HEADERS = { accept: 'application/json' };

    /**
     * Build a gateway URL. Parameters go through URLSearchParams so the query
     * always starts with `?`: appending one with `&` makes the gateway read it as
     * part of the ID and reject the request with a 400 (which surfaces in the
     * browser as a CORS error).
     */
    const gatewayUrl = (path: string): string => {
        const params = new URLSearchParams();

        if (profileKey) {
            params.set('tn_profilekey', profileKey);
        }

        const query = params.toString();

        return `https://gateway.tweakwisenavigator.com/${path}${query ? `?${query}` : ''}`;
    };

    /**
     * Get featured products from Tweakwise
     * @param displayId - The display ID for the featured products
     * @returns Promise with the featured products. Item ids in the response are
     *          Tweakwise ids: convert them with shopwareIdsFromItems() before
     *          querying Shopware.
     */
    const getFeaturedProducts = async (displayId: string): Promise<TweakwiseRecommendationsResponse> => {
        try {
            const url = gatewayUrl(`recommendations/featured/${instanceKey}/${displayId}`);

            const response = await fetch(url, { headers: JSON_HEADERS });

            if (!response.ok) {
                throw new Error(`Failed to fetch featured products: ${response.status} ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching Tweakwise featured products:', error);
            return {
                products: [],
                statusCode: 500,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    };

    /**
     * Get single product recommendations from Tweakwise
     * @param itemNo - The TWEAKWISE item id ({crc32hex(domainId)}-{shopwareProductId}),
     *                 not a bare Shopware product ID — see ~/utils/tweakwise
     * @param ruleId - The recommendation rule ID
     * @returns Promise with the recommended products. Item ids in the response are
     *          Tweakwise ids: convert them with shopwareIdsFromItems() before
     *          querying Shopware.
     */
    const getProductRecommendations = async (
        itemNo: string,
        ruleId: string,
    ): Promise<TweakwiseRecommendationsResponse> => {
        try {
            const url = gatewayUrl(`recommendations/product/${instanceKey}/${ruleId}/${itemNo}`);

            const response = await fetch(url, { headers: JSON_HEADERS });

            if (!response.ok) {
                throw new Error(`Failed to fetch product recommendations: ${response.status} ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching Tweakwise product recommendations:', error);
            return {
                products: [],
                statusCode: 500,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    };

    return {
        getFeaturedProducts,
        getProductRecommendations,
        profileKey,
    };
}
